import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { ProbabilisticBundleReward } from '@sorare/core/src/components/3d/ProbabilisticBundleReward';
import { glossary } from '@sorare/core/src/lib/glossary';
import { lazy } from '@sorare/core/src/lib/retry';

import { Shape } from '../Shape';
import { useWheelReward } from '../utils';
import type {
  Reward_probabilisticBundleSlotConfig,
  Reward_probabilisticBundleSlotItemInterface,
} from './__generated__/index.graphql';

const Scene = lazy(
  async () => import('@sorare/trois/src/components/Scene'),
  'Scene'
);

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  align-content: center;
  justify-content: center;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: var(--c-nl-700);
    z-index: -1;
  }
`;

const DescriptionContainer = styled.div`
  position: absolute;
  bottom: var(--double-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  text-align: center;
`;

type Props = {
  item: Reward_probabilisticBundleSlotItemInterface;
  config: Reward_probabilisticBundleSlotConfig;
  rarity: Rarity;
  onClose: () => void;
};

export const Reward = ({ item, config, rarity, onClose }: Props) => {
  const { description: shortDescription, longDescription } =
    useWheelReward(config);
  const description = shortDescription || longDescription;
  return (
    <Wrapper onClick={onClose}>
      <Vertical center gap={3}>
        <BrandL className="text-center">
          <FormattedMessage id="Reward.youWon" defaultMessage="You've won:" />
        </BrandL>
        <Shape
          config={config}
          style={{
            viewTransitionName: 'wheel-reward',
          }}
          rarity={rarity}
        >
          <Vertical
            style={{
              height: '400px',
              position: 'relative',
              minWidth: 'min(300px, 100vw)',
            }}
            center
          >
            <Suspense fallback={null}>
              <Scene fallback={null}>
                <ProbabilisticBundleReward item={item} />
              </Scene>
            </Suspense>
            <DescriptionContainer>
              <BrandL>{description}</BrandL>
            </DescriptionContainer>
          </Vertical>
        </Shape>
        <Button color="primary" size="large" onClick={onClose}>
          <FormattedMessage {...glossary.ok} />
        </Button>
      </Vertical>
    </Wrapper>
  );
};

Reward.fragments = {
  probabilisticBundleSlotItemInterface: gql`
    fragment Reward_probabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ...ProbabilisticBundleReward_probabilisticBundleSlotItemInterface
    }
    ${ProbabilisticBundleReward.fragments.probabilisticBundleSlotItemInterface}
  ` as TypedDocumentNode<Reward_probabilisticBundleSlotItemInterface>,
  probabilisticBundleSlotConfig: gql`
    fragment Reward_probabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
      id
      ...Shape_probabilisticBundleSlotConfig
      ...useWheelReward_probabilisticBundleSlotConfig
    }
    ${Shape.fragments.ProbabilisticBundleSlotConfig}
    ${useWheelReward.fragments.probabilisticBundleSlotConfig}
  ` as TypedDocumentNode<Reward_probabilisticBundleSlotConfig>,
};
